import axios from 'axios';
import { config } from '../config';

const api = axios.create({
  baseURL: config.API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface ChatMessage {
  message: string;
  session_id: string;
  conversationId?: string;
  userId?: string;
}

export interface ChatResponse {
  conversationId: string;
  response: string;
  timestamp: number;
}

export interface Conversation {
  conversationId: string;
  userId: string;
  messages: Array<{
    timestamp: number;
    userMessage: string;
    botResponse: string;
  }>;
  createdAt: string;
  lastActivity: string;
}

export const chatAPI = {
  sendMessage: async (data: ChatMessage): Promise<ChatResponse> => {
    const response = await api.post('/chat', data);
    return response.data;
  },

  sendMessageStream: async (
    data: ChatMessage,
    onChunk: (text: string) => void,
    onComplete: () => void,
    onError: (error: string) => void
  ): Promise<void> => {
    try {
      const response = await fetch(`${config.API_URL}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error('Stream request failed');
      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.startsWith('data: '));

        for (const line of lines) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            onComplete();
            return;
          }
          try {
            const parsed = JSON.parse(data);
            if (parsed.text) onChunk(parsed.text);
            if (parsed.error) onError(parsed.error);
          } catch (e) {
            console.error('Failed to parse chunk:', e);
          }
        }
      }
      onComplete();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Stream failed');
    }
  },
  
  transcribeAndSend: async (formData: FormData): Promise<ChatResponse & { transcription: string }> => {
    const response = await api.post('/voice', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export const adminAPI = {
  getConversations: async (token: string, limit = 50, lastKey?: string) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (lastKey) params.append('lastKey', lastKey);
    
    const response = await api.get(`/admin/conversations?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};
